import { useEffect, useState, type FormEvent } from 'react';
import type {
  Attachment,
  Cell,
  Turn as HistoryTurn,
  TakeoffResult,
  TakeoffSheet,
  ToolCall,
} from '@manudev/assistant';
import { useHeroData } from '@/hero/data';
import { MODELS, useLocalModel } from '@/llm/model';
import { useSelectionStore } from '@/state/selection';
import { useTourStore } from '@/state/tour';
import { ask, COMMANDS, saveAttachment, SUGGESTED, type Answer } from './ask';
import { useAssistantStore } from './state';

interface Turn {
  q: string;
  a?: Answer;
}

export function AssistantPanel() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const data = useHeroData((s) => s.data);
  const setHighlighted = useSelectionStore((s) => s.setHighlighted);
  const setEffects = useSelectionStore((s) => s.setEffects);
  const local = useLocalModel();
  const pending = useAssistantStore((s) => s.pending);
  const take = useAssistantStore((s) => s.take);

  const submit = async (q: string) => {
    const question = q.trim();
    if (!question || busy) return;
    setInput('');
    setBusy(true);
    const history: HistoryTurn[] = turns
      .filter((t): t is Required<Turn> => !!t.a)
      .map((t) => ({ question: t.q, answer: t.a.text, suggest: t.a.suggest }));
    setTurns((t) => [...t.slice(-5), { q: question }]);
    const a = await ask(question, data, history);
    setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, a } : x)));
    setHighlighted(a.highlights);
    if (a.effects?.length) setEffects(a.effects);
    setBusy(false);
  };

  useEffect(() => {
    if (!pending || busy) return;
    // defer past the render so the queued question does not cascade renders
    const t = setTimeout(() => {
      const q = take();
      if (q) void submit(q);
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, busy]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submit(input);
  };

  return (
    <div className="lens-panel assistant">
      <div className="lens-bar">
        <span className="lens-dot" />
        <span>ask and build</span>
        <span className="lens-right">{viaLabel(turns.at(-1)?.a?.via, local.model.label)}</span>
      </div>
      <LocalModelRow />
      <div className="chat">
        {turns.length === 0 && (
          <div className="suggest">
            {SUGGESTED.map((s) => (
              <button key={s} type="button" onClick={() => void submit(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className="turn">
            <p className="q">{t.q}</p>
            {t.a ? (
              <>
                {(t.a.trace.length > 0 || t.a.raw?.length) && (
                  <pre className="trace">
                    {[
                      ...(t.a.raw ?? []).map((r) => `model › ${clip(r)}`),
                      ...t.a.trace.map((l) => `› ${l}`),
                    ].join('\n')}
                  </pre>
                )}
                <p className="a">{t.a.text}</p>
                <Quantities
                  calls={t.a.calls}
                  busy={busy}
                  onExport={() => void submit('Export the takeoff as Excel')}
                />
                {t.a.unverified && t.a.unverified.length > 0 && (
                  <p className="a warn">Not backed by a tool result: {t.a.unverified.join(', ')}</p>
                )}
                {t.a.note && <p className="a warn">{t.a.note}</p>}
              </>
            ) : local.live ? (
              <pre className="trace live">model › {clip(local.live, 400)}</pre>
            ) : (
              <p className="a thinking">…</p>
            )}
          </div>
        ))}
      </div>
      <div className="commands suggest" aria-label="Build commands">
        <span className="lens-right">build</span>
        {COMMANDS.map((c) => (
          <button key={c} type="button" disabled={busy} onClick={() => void submit(c)}>
            {c}
          </button>
        ))}
      </div>
      <form className="ask" onSubmit={onSubmit}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about this building"
          disabled={busy}
          aria-label="Question"
        />
        <button type="submit" disabled={busy || !input.trim()}>
          Ask
        </button>
      </form>
    </div>
  );
}

// The takeoff as a table: the sheets of an export, or the per-type split of a plain takeoff,
// with the file one click away either way.
function Quantities({
  calls,
  busy,
  onExport,
}: {
  calls: ToolCall[];
  busy: boolean;
  onExport: () => void;
}) {
  const [tab, setTab] = useState(0);
  const exported = [...calls]
    .reverse()
    .find((c) => c.tool === 'export_takeoff' && sheetsOf(c.output));
  if (exported) {
    const { sheets, attachment } = exported.output as {
      sheets: TakeoffSheet[];
      attachment: Attachment;
    };
    const sheet = sheets[Math.min(tab, sheets.length - 1)]!;
    return (
      <div className="qty">
        <div className="qty-bar">
          {sheets.length > 1 &&
            sheets.map((s, i) => (
              <button
                key={s.name}
                type="button"
                aria-pressed={s === sheet}
                onClick={() => setTab(i)}
              >
                {s.name}
              </button>
            ))}
          <button type="button" className="qty-save" onClick={() => saveAttachment(attachment)}>
            Download {attachment.name.split('.').pop()}
          </button>
        </div>
        <Table columns={sheet.columns} rows={sheet.rows} totals={sheet.totals} />
      </div>
    );
  }
  const call = [...calls].reverse().find((c) => c.tool === 'quantity_takeoff');
  const r = call?.output as TakeoffResult | undefined;
  if (!r || r.quantity === 'count' || !Object.keys(r.by_type ?? {}).length) return null;
  const unit = r.unit === 'm3' ? 'm³' : 'm²';
  const rows: Cell[][] = Object.entries(r.by_type)
    .sort((a, b) => b[1] - a[1])
    .map(([type, v]) => [type, v]);
  return (
    <div className="qty">
      <div className="qty-bar">
        <button type="button" className="qty-save" disabled={busy} onClick={onExport}>
          Excel
        </button>
      </div>
      <Table
        columns={['Type', `${r.quantity === 'volume' ? 'Volume' : 'Area'} (${unit})`]}
        rows={rows}
        totals={['Total', r.total]}
      />
    </div>
  );
}

function Table({ columns, rows, totals }: { columns: string[]; rows: Cell[][]; totals: Cell[] }) {
  return (
    <div className="qty-scroll">
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((c, j) => (
                <td key={j}>{cellText(c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            {totals.map((c, j) => (
              <td key={j}>{cellText(c)}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

const sheetsOf = (out: unknown) => Array.isArray((out as { sheets?: unknown } | null)?.sheets);

const cellText = (c: Cell) =>
  c == null ? '–' : typeof c === 'number' ? c.toLocaleString('en-GB') : c;

function viaLabel(via: Answer['via'] | undefined, model: string): string {
  return via === 'local' ? `browser · ${model.toLowerCase()}` : 'local · rules';
}

function LocalModelRow() {
  const {
    status,
    model,
    device,
    dtype,
    f16,
    gpu,
    progress,
    loadedTotal,
    error,
    last,
    generations,
    load,
    choose,
    probe,
    probeModel,
  } = useLocalModel();
  const backend = useTourStore((s) => s.backend);
  if (status === 'unsupported') return null;
  if (status === 'ready')
    return (
      <div className="local-row ready">
        <span className="stats">
          <b>{model.label}</b> · {device} · {dtype}
          {!f16 && device === 'webgpu' ? ' (no f16 on this GPU)' : ''}
          {gpu ? ` · ${gpu}` : ''} · {generations} runs
          {last
            ? ` · ${((last.tokens / last.ms) * 1000).toFixed(1)} tok/s · first token ${(last.firstTokenMs / 1000).toFixed(2)} s`
            : ''}{' '}
          · 0 bytes to a server · tools: MCP, in page · render: {backend ?? '–'}
        </span>
        <button type="button" onClick={() => void probeModel()}>
          Test
        </button>
        {probe && (
          <pre className="trace probe">
            {'error' in probe
              ? `error: ${probe.error}`
              : `${clip(probe.text, 300)}\n${probe.tokens} tokens in ${(probe.ms / 1000).toFixed(1)} s (${((probe.tokens / probe.ms) * 1000).toFixed(1)} tok/s)`}
          </pre>
        )}
      </div>
    );
  if (status === 'loading') {
    const pct = Math.min(100, Math.round((loadedTotal / (model.sizeMB[dtype] * 1e6)) * 100));
    return (
      <div className="local-row">
        <span>
          loading {model.label} · {pct}%{progress ? ` · ${progress.file.split('/').pop()}` : ''}
        </span>
        <div className="local-bar">
          <div style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }
  return (
    <div className="local-row">
      <span>
        {status === 'error'
          ? `browser model failed: ${error}`
          : 'Run a language model in your browser. Weights download once and stay cached.'}
      </span>
      <select value={model.id} onChange={(e) => choose(e.target.value)} aria-label="Model">
        {MODELS.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label} · {m.sizeMB.q4f16} MB · {m.note}
          </option>
        ))}
      </select>
      <button type="button" onClick={load}>
        {status === 'error' ? 'Retry' : 'Load'}
      </button>
    </div>
  );
}

const clip = (s: string, n = 220) => (s.length > n ? s.slice(0, n) + '…' : s).replace(/\n+/g, ' ');
